import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  sessionexlist,
  grpplayerlist,
  listSessionSubgroups,
} from "../components/funtionsBD/Mainservice";
import {
  resolveViewerTableContext,
  mergeSocketSequenceMeta,
  pickLiveRolesFromSocketPayload,
  payloadHasSequenceMeta,
} from "../utils/liveTrainingPlayerContext";
import { normalizeSessionTablesForRoster } from "../utils/coachSessionTables";
import {
  findViewerSubgroupId,
  pickStationScopedExerciseIndex,
} from "../utils/tableSeats";
import {
  buildLiveStations,
  hasMultipleLiveStations,
  nextCoachIndexAfterStateChange,
  persistLiveTrainingTsgId,
  pickCoachStationControlIndex,
  resolveFocusedTsgId,
} from "../utils/liveTrainingStations";
import { fetchSessionTablesDeduped } from "../utils/sessionTableFetch";
import { currentSequenceFromLiveIndex } from "../utils/liveTrainingSequenceResolve";
import { initializeSocket } from "../socket";
import {
  TE_STATE_FINALIZED,
  TE_STATE_CANCELLED,
  TE_STATE_STARTED,
  resolveExerciseStateIdFromRow,
  buildRealtimeMapFromExerciseRows,
} from "../utils/coachExerciseNavigation";
import { isSessionFinalized } from "../utils/liveTrainingSessionWindow";
import {
  patchExercisesFromSyncDomains,
  requestSessionSync,
  noteExerciseTimerRevision,
  applyServerRevisions,
} from "../utils/sessionSync";
import {
  applyForcedConstraints,
  publishLiveTrainingClientConstraints,
  loadCoachSessionSettings,
} from "../utils/liveTrainingSettings";
import { patchExerciseMediaClock } from "../utils/liveTrainingMediaSync";
import { isKotExercise } from "../utils/teExecutionType";
import {
  applyKotMatchSubmittedPatch,
  applyKotRoundCompletedPatch,
  mergeKotSnapshots,
} from "../utils/kotCoachRound";
import { useLiveHrHistory } from "./useLiveHrHistory";
import {
  readAwaitingAutoSequenceStart,
  readSequenceTransitionPauseRemaining,
} from "../utils/sequenceTransitionPause";
import {
  isCoachViewForTraining,
  resolveViewerPlayerId,
} from "../utils/trainingRoomAccess";
import { usePermissions } from "../context/Permissioncontext";

const SIDE_BY_SLOT = { a: "A", b: "B", c: "C", d: "D" };

/** Map socket/API currentSequence (1-based order or row) → 0-based index. */
function sequenceIndexFromCurrentSequence(raw) {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === "number") {
    const idx = Math.max(0, raw - 1);
    return Number.isFinite(idx) ? idx : null;
  }
  if (typeof raw === "object" && raw?.sequence_order != null) {
    const idx = Math.max(0, Number(raw.sequence_order) - 1);
    return Number.isFinite(idx) ? idx : null;
  }
  return null;
}

/** Merge exercise_timer_snapshots into currentSequenceIndexByTeId map. */
function mergeSequenceIndexFromTimerSnaps(prev, timerSnaps) {
  if (!timerSnaps || typeof timerSnaps !== "object") return prev;
  let next = prev;
  for (const [tid, snap] of Object.entries(timerSnaps)) {
    if (snap?.currentSequence === undefined) continue;
    const sequenceIndex = sequenceIndexFromCurrentSequence(snap.currentSequence);
    if (sequenceIndex == null) continue;
    if (next === prev) next = { ...prev };
    next[String(tid)] = sequenceIndex;
  }
  return next;
}

/** Find the viewer's role/side from a socket payload carrying player_{a..d}_id/role slots. */
function deriveViewerSlot(payload, viewerPlayerId, viewerUserId) {
  if (!payload) return null;
  const pid = viewerPlayerId != null ? String(viewerPlayerId) : null;
  const uid = viewerUserId != null ? String(viewerUserId) : null;
  for (const slot of ["a", "b", "c", "d"]) {
    const slotPid = payload[`player_${slot}_id`];
    if (slotPid == null) continue;
    if ((pid && String(slotPid) === pid) || (uid && String(slotPid) === uid)) {
      return {
        side: SIDE_BY_SLOT[slot],
        role: payload[`player_${slot}_role`] || null,
      };
    }
  }
  return null;
}

/** Extract the viewer's live BPM from an hr-update / player_heart_rate payload.
 * Returns `{ matched: true, bpm }` when the payload mentions this viewer (`bpm` null = cleared),
 * or `{ matched: false }` when the event is about someone else / no viewer slot.
 */
function extractViewerHrState(payload, viewerPlayerId, viewerUserId) {
  if (!payload) return { matched: false };
  const pid = viewerPlayerId != null ? String(viewerPlayerId) : null;
  const uid = viewerUserId != null ? String(viewerUserId) : null;
  const matches = (p) => {
    if (!p) return false;
    const ppid = p.player_id != null ? String(p.player_id) : null;
    const puid = p.user_id != null ? String(p.user_id) : null;
    return (pid && ppid === pid) || (uid && puid === uid);
  };
  const readBpm = (p) => {
    const hr = Number(p?.hr_value ?? p?.heart_rate);
    if (!Number.isFinite(hr) || hr <= 0) return null;
    const recordedAt = p?.recorded_at;
    if (recordedAt != null && recordedAt !== "") {
      const t = new Date(recordedAt).getTime();
      if (Number.isFinite(t) && Date.now() - t > 12000) return null;
    }
    return hr;
  };

  const tables = Array.isArray(payload.tables) ? payload.tables : null;
  if (tables) {
    for (const tbl of tables) {
      const players = Array.isArray(tbl?.players) ? tbl.players : [];
      for (const p of players) {
        if (matches(p)) return { matched: true, bpm: readBpm(p) };
      }
    }
    return { matched: false };
  }
  if (matches(payload)) return { matched: true, bpm: readBpm(payload) };
  return { matched: false };
}

export function useLiveTrainingSession(session) {
  const tsId = session?.ts_id;
  const grId = session?.gr_id;
  const { permissions } = usePermissions();
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem("user") || "{}")
  );

  useEffect(() => {
    const syncUser = () => {
      try {
        setUser(JSON.parse(localStorage.getItem("user") || "{}"));
      } catch {
        setUser({});
      }
    };
    window.addEventListener("storage", syncUser);
    window.addEventListener("smarttt-user-updated", syncUser);
    return () => {
      window.removeEventListener("storage", syncUser);
      window.removeEventListener("smarttt-user-updated", syncUser);
    };
  }, []);

  const [exercises, setExercises] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [focusedTsgId, setFocusedTsgIdState] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeByTeId, setRealtimeByTeId] = useState({});
  const [kotSnapshot, setKotSnapshot] = useState(null);
  const [sessionTablesRaw, setSessionTablesRaw] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewerHr, setViewerHr] = useState(null);
  const [maxHrByTeId, setMaxHrByTeId] = useState({});
  const [seqMetaByTeId, setSeqMetaByTeId] = useState({});
  const [liveSequenceRolesByTeId, setLiveSequenceRolesByTeId] = useState({});
  const [socketSequenceMetaByTeId, setSocketSequenceMetaByTeId] = useState({});
  const [currentSequenceIndexByTeId, setCurrentSequenceIndexByTeId] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [viewRefreshToken, setViewRefreshToken] = useState(0);
  const [sessionFinalized, setSessionFinalized] = useState(() =>
    isSessionFinalized(session)
  );
  /** All exercises terminal; coach should finalize session or add another exercise. */
  const [awaitingSessionDecision, setAwaitingSessionDecision] = useState(false);
  const socketRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const lastRefreshAtRef = useRef(0);
  const refreshGenerationRef = useRef(0);
  const allowAutoRefreshRef = useRef(false);
  const rosterRef = useRef([]);
  const viewerPlayerIdRef = useRef(null);
  const currentTeIdRef = useRef(null);
  const currentExerciseRef = useRef(null);
  const currentIndexRef = useRef(0);
  const focusedTsgIdRef = useRef(null);
  const realtimeByTeIdRef = useRef(realtimeByTeId);
  const currentSequenceIndexByTeIdRef = useRef(currentSequenceIndexByTeId);
  const rosterLoadInFlightRef = useRef(null);
  const lastRosterLoadAtRef = useRef(0);
  const sessionTablesTeIdRef = useRef(null);
  const finalizedDispatchedRef = useRef(new Set());

  const persistSessionFinalized = useCallback(() => {
    if (!tsId) return;
    try {
      const stored = JSON.parse(localStorage.getItem("rsessn") || "null");
      if (!stored?.ts_id || String(stored.ts_id) !== String(tsId)) return;
      const updated = {
        ...stored,
        te_state: TE_STATE_FINALIZED,
        is_completed: true,
      };
      localStorage.setItem("rsessn", JSON.stringify(updated));
      window.dispatchEvent(new Event("smarttt-session-changed"));
    } catch {
      /* ignore */
    }
  }, [tsId]);

  const reopenSessionAfterAddExercise = useCallback(() => {
    if (!tsId) return;
    setSessionFinalized(false);
    setAwaitingSessionDecision(false);
    try {
      const stored = JSON.parse(localStorage.getItem("rsessn") || "null");
      if (!stored?.ts_id || String(stored.ts_id) !== String(tsId)) return;
      const updated = {
        ...stored,
        te_state: TE_STATE_STARTED,
        is_completed: false,
        tbl_state_te: stored.tbl_state_te
          ? { ...stored.tbl_state_te, st_id: TE_STATE_STARTED }
          : { st_id: TE_STATE_STARTED },
        state_te: stored.state_te
          ? { ...stored.state_te, st_id: TE_STATE_STARTED }
          : { st_id: TE_STATE_STARTED },
      };
      localStorage.setItem("rsessn", JSON.stringify(updated));
      window.dispatchEvent(new Event("smarttt-session-changed"));
    } catch {
      /* ignore */
    }
  }, [tsId]);

  const markSessionFinalized = useCallback(() => {
    setAwaitingSessionDecision(false);
    setSessionFinalized(true);
    persistSessionFinalized();
  }, [persistSessionFinalized]);

  useEffect(() => {
    if (isSessionFinalized(session)) {
      markSessionFinalized();
    }
  }, [session, markSessionFinalized]);

  useEffect(() => {
    if (sessionFinalized || !exercises.length) {
      if (sessionFinalized) setAwaitingSessionDecision(false);
      return;
    }
    const allDone = exercises.every((ex) => {
      const sid = resolveExerciseStateIdFromRow(ex, realtimeByTeId);
      return sid === TE_STATE_FINALIZED || sid === TE_STATE_CANCELLED;
    });
    if (allDone) {
      setAwaitingSessionDecision(true);
    } else {
      setAwaitingSessionDecision(false);
    }
  }, [exercises, realtimeByTeId, sessionFinalized]);

  const socket = useMemo(() => {
    if (!token) return null;
    return initializeSocket(token);
  }, [token]);
  socketRef.current = socket;

  const viewerPlayerId = useMemo(
    () => resolveViewerPlayerId(user, roster),
    [user, roster]
  );

  const currentExercise = exercises[currentIndex] || null;
  const currentTeId = currentExercise?.te_id;

  useEffect(() => {
    currentTeIdRef.current = currentTeId ?? null;
    currentExerciseRef.current = currentExercise ?? null;
    currentIndexRef.current = currentIndex;
    focusedTsgIdRef.current = focusedTsgId;
    realtimeByTeIdRef.current = realtimeByTeId;
    currentSequenceIndexByTeIdRef.current = currentSequenceIndexByTeId;
  }, [
    currentTeId,
    currentExercise,
    currentIndex,
    focusedTsgId,
    realtimeByTeId,
    currentSequenceIndexByTeId,
  ]);

  useEffect(() => {
    rosterRef.current = roster;
    viewerPlayerIdRef.current = viewerPlayerId;
  }, [roster, viewerPlayerId]);

  const stations = useMemo(
    () => buildLiveStations({ subgroups, exercises }),
    [subgroups, exercises]
  );

  const multiStation = hasMultipleLiveStations(stations);

  const setFocusedTsgId = useCallback((tsgId) => {
    const next = tsgId != null ? String(tsgId) : null;
    focusedTsgIdRef.current = next;
    setFocusedTsgIdState(next);
    persistLiveTrainingTsgId(next);
  }, []);

  const loadSubgroups = useCallback(async () => {
    if (!tsId) return [];
    try {
      const response = await listSessionSubgroups({ ts_id: tsId });
      const data = response?.data?.data || [];
      setSubgroups(Array.isArray(data) ? data : []);
      return data;
    } catch {
      setSubgroups([]);
      return [];
    }
  }, [tsId]);

  const selectFocusedStation = useCallback(
    (tsgId) => {
      // Honor an explicit station id immediately (e.g. just-created station
      // before `stations` has refreshed). Fall back to resolve when clearing.
      const nextId =
        tsgId != null && String(tsgId)
          ? String(tsgId)
          : resolveFocusedTsgId(stations, null);
      setFocusedTsgId(nextId);
      setCurrentIndex((prev) => {
        const idx = pickCoachStationControlIndex(
          exercises,
          realtimeByTeIdRef.current,
          nextId
        );
        if (idx >= 0) return idx;
        // Keep previous only when it already belongs to the focused station.
        const prevEx = exercises[prev];
        if (
          nextId &&
          prevEx &&
          String(prevEx.tsg_id || "") === String(nextId)
        ) {
          return prev;
        }
        const firstOnStation = (exercises || []).findIndex(
          (ex) => String(ex?.tsg_id || "") === String(nextId || "")
        );
        return firstOnStation >= 0 ? firstOnStation : prev;
      });
    },
    [stations, exercises, setFocusedTsgId]
  );

  const refreshExercises = useCallback(async ({ skipIndexUpdate = false } = {}) => {
    if (!tsId) return [];
    try {
      const res = await sessionexlist({ ts_id: tsId });
      const list = res?.data?.data || [];
      const sorted = [...list].sort((a, b) => (a.te_order ?? 0) - (b.te_order ?? 0));
      // API plan rows have no live sequence/timers — preserve socket state by te_id.
      let merged = sorted;
      setExercises((prev) => {
        const prevById = new Map(prev.map((ex) => [String(ex.te_id), ex]));
        const liveIdxMap = currentSequenceIndexByTeIdRef.current || {};
        merged = sorted.map((ex) => {
          const key = String(ex.te_id);
          const old = prevById.get(key);
          const fromLive = currentSequenceFromLiveIndex(ex, liveIdxMap[key]);
          if (!old && fromLive === undefined) return ex;
          return {
            ...ex,
            timer: old?.timer ?? ex.timer,
            sequenceTimer: old?.sequenceTimer ?? ex.sequenceTimer,
            currentSequence:
              fromLive !== undefined
                ? fromLive
                : old?.currentSequence !== undefined
                  ? old.currentSequence
                  : ex.currentSequence,
            synchronizedStartTime:
              old?.synchronizedStartTime ?? ex.synchronizedStartTime,
            sequenceStartTime: old?.sequenceStartTime ?? ex.sequenceStartTime,
            media_sync_at: old?.media_sync_at ?? ex.media_sync_at,
            mediaSyncAt: old?.mediaSyncAt ?? ex.mediaSyncAt,
            exercise_timer_revision:
              old?.exercise_timer_revision ?? ex.exercise_timer_revision,
          };
        });
        return merged;
      });
      if (!skipIndexUpdate) {
        setCurrentIndex((prev) => {
          const coachFocus = isCoachViewForTraining(user, permissions)
            ? focusedTsgIdRef.current
            : null;
          const idx = pickCoachStationControlIndex(
            merged,
            realtimeByTeIdRef.current,
            coachFocus
          );
          return idx >= 0 ? idx : prev;
        });
      }
      return merged;
    } catch (e) {
      setError(e?.message || "Failed to load exercises");
      return [];
    }
  }, [tsId, user, permissions]);

  const loadRoster = useCallback(async ({ force = false } = {}) => {
    if (!tsId || !grId) return [];
    const now = Date.now();
    if (!force && now - lastRosterLoadAtRef.current < 2500) {
      return rosterRef.current;
    }
    if (rosterLoadInFlightRef.current) {
      return rosterLoadInFlightRef.current;
    }

    const run = (async () => {
      try {
        const res = await grpplayerlist({ ts_id: tsId, gr_id: grId });
        const data = res?.data?.data || [];
        setRoster(data);
        lastRosterLoadAtRef.current = Date.now();
        return data;
      } catch {
        setRoster([]);
        return [];
      } finally {
        rosterLoadInFlightRef.current = null;
      }
    })();

    rosterLoadInFlightRef.current = run;
    return run;
  }, [tsId, grId]);

  const requestRefresh = useCallback(
    (te_id, patch) => {
      if (patch && te_id) {
        noteExerciseTimerRevision(te_id, patch.exercise_timer_revision);
        setRealtimeByTeId((prev) => ({
          ...prev,
          [String(te_id)]: patch.te_state || patch.tbl_state_te?.st_id || prev[String(te_id)],
        }));
        setExercises((prev) =>
          prev.map((ex) =>
            String(ex.te_id) === String(te_id)
              ? patchExerciseMediaClock(
                  {
                    ...ex,
                    te_state: patch.te_state ?? ex.te_state,
                    tbl_state_te: patch.tbl_state_te ?? ex.tbl_state_te,
                    timer: patch.timer ?? patch.remainingTime ?? ex.timer,
                    sequenceTimer:
                      patch.sequenceTimer ??
                      patch.sequenceRemainingTime ??
                      ex.sequenceTimer,
                    currentSequence:
                      patch.currentSequence !== undefined
                        ? patch.currentSequence
                        : ex.currentSequence,
                  },
                  patch
                )
              : ex
          )
        );
      } else {
        refreshExercises();
      }
    },
    [refreshExercises]
  );

  useEffect(() => {
    if (!tsId || !socket || !user?.user_id) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      await Promise.all([refreshExercises(), loadRoster()]);
      if (cancelled) return;

      const role = isCoachViewForTraining(user, permissions) ? "coach" : "player";
      socket.emit("joinTrainingSession", {
        ts_id: tsId,
        user_id: user.user_id,
        role,
      });

      // Rehydrate server in-memory constraints after coach joins (e.g. API restart).
      if (role === "coach") {
        publishLiveTrainingClientConstraints(
          socket,
          tsId,
          loadCoachSessionSettings(tsId)
        );
      }

      await requestSessionSync(socket, {
        tsId,
        onDomains: (domains) => {
          setExercises((prev) => patchExercisesFromSyncDomains(prev, domains));
          if (domains?.exercise_timer_snapshots) {
            setCurrentSequenceIndexByTeId((prev) =>
              mergeSequenceIndexFromTimerSnaps(prev, domains.exercise_timer_snapshots)
            );
          }
          if (domains?.kot_snapshots && currentTeId) {
            const snap = domains.kot_snapshots[String(currentTeId)];
            if (snap) setKotSnapshot((p) => mergeKotSnapshots(p, snap));
          }
        },
      });
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [tsId, socket, user, permissions]);

  useEffect(() => {
    if (!socket || !tsId || !user?.user_id) return;
    const rejoin = () => {
      const role = isCoachViewForTraining(user, permissions) ? "coach" : "player";
      socket.emit("joinTrainingSession", {
        ts_id: tsId,
        user_id: user.user_id,
        role,
      });
      if (role === "coach") {
        publishLiveTrainingClientConstraints(
          socket,
          tsId,
          loadCoachSessionSettings(tsId)
        );
      }
    };
    socket.on("connect", rejoin);
    return () => {
      socket.off("connect", rejoin);
    };
  }, [socket, tsId, user, permissions]);

  useEffect(() => {
    if (!loading && tsId) {
      allowAutoRefreshRef.current = true;
    }
  }, [loading, tsId]);

  useEffect(() => {
    if (!socket || !tsId) return;

    const applyLiveSequencePayload = (data) => {
      if (!data?.te_id) return;
      const teId = String(data.te_id);
      if (data.currentSequence !== undefined) {
        const sequenceIndex = sequenceIndexFromCurrentSequence(data.currentSequence);
        if (sequenceIndex != null) {
          setCurrentSequenceIndexByTeId((prev) => ({
            ...prev,
            [teId]: sequenceIndex,
          }));
        }
      }
      if (
        data.exercise_slot_roles != null ||
        data.player_a_role !== undefined ||
        data.player_b_role !== undefined
      ) {
        setLiveSequenceRolesByTeId((prev) => ({
          ...prev,
          [teId]: pickLiveRolesFromSocketPayload(data),
        }));
      }
      if (payloadHasSequenceMeta(data)) {
        setSocketSequenceMetaByTeId((prev) => ({
          ...prev,
          [teId]: mergeSocketSequenceMeta(prev[teId], data),
        }));
      }
      if (
        data.awaiting_manual_sequence_start != null ||
        data.awaitingManualSequenceStart != null ||
        data.awaiting_auto_sequence_start != null ||
        data.awaitingAutoSequenceStart != null ||
        data.sequence_transition_pause_remaining != null ||
        data.sequenceTransitionPauseRemaining != null
      ) {
        const pauseRem = readSequenceTransitionPauseRemaining(data);
        const awaitingAuto = readAwaitingAutoSequenceStart(data);
        setSeqMetaByTeId((prev) => ({
          ...prev,
          [teId]: {
            ...prev[teId],
            awaitingManualSequenceStart:
              data.awaiting_manual_sequence_start ??
              data.awaitingManualSequenceStart ??
              prev[teId]?.awaitingManualSequenceStart ??
              false,
            awaitingAutoSequenceStart:
              data.awaiting_auto_sequence_start != null ||
              data.awaitingAutoSequenceStart != null
                ? awaitingAuto
                : prev[teId]?.awaitingAutoSequenceStart ?? false,
            sequenceTransitionPauseRemaining:
              pauseRem != null
                ? pauseRem
                : awaitingAuto
                  ? prev[teId]?.sequenceTransitionPauseRemaining ?? 0
                  : 0,
          },
        }));
      }
      const stateId = data.te_state || data.tbl_state_te?.st_id;
      if (
        stateId === TE_STATE_FINALIZED ||
        stateId === TE_STATE_CANCELLED ||
        stateId === "finalized" ||
        stateId === "cancelled"
      ) {
        setLiveSequenceRolesByTeId((prev) => {
          const next = { ...prev };
          delete next[teId];
          return next;
        });
        setSocketSequenceMetaByTeId((prev) => {
          const next = { ...prev };
          delete next[teId];
          return next;
        });
        setCurrentSequenceIndexByTeId((prev) => {
          const next = { ...prev };
          delete next[teId];
          return next;
        });
        setSeqMetaByTeId((prev) => {
          const next = { ...prev };
          delete next[teId];
          return next;
        });
      }
    };

    const onExerciseState = (data) => {
      if (!data?.te_id) return;
      const teKey = String(data.te_id);
      const stateId = data.te_state || data.tbl_state_te?.st_id;
      if (stateId === TE_STATE_STARTED) {
        finalizedDispatchedRef.current.delete(teKey);
      }
      requestRefresh(data.te_id, data);
      applyLiveSequencePayload(data);
      if (stateId === TE_STATE_FINALIZED) {
        if (!finalizedDispatchedRef.current.has(teKey)) {
          finalizedDispatchedRef.current.add(teKey);
          window.dispatchEvent(
            new CustomEvent("smarttt-exercise-finalized", { detail: { te_id: data.te_id } })
          );
        }
      }
      setCurrentIndex((prev) => {
        const mergedRealtime = {
          ...realtimeByTeId,
          [teKey]: stateId,
        };
        return nextCoachIndexAfterStateChange({
          exercises,
          realtimeByTeId: mergedRealtime,
          prevIndex: prev,
          changedTeId: data.te_id,
          focusedTsgId: focusedTsgIdRef.current,
        });
      });
    };

    const onTimer = (data) => {
      if (!data?.te_id) return;
      applyLiveSequencePayload(data);
      setExercises((prev) =>
        prev.map((ex) =>
          String(ex.te_id) === String(data.te_id)
            ? patchExerciseMediaClock(
                {
                  ...ex,
                  timer: data.timer ?? data.remainingTime ?? ex.timer,
                  sequenceTimer:
                    data.sequenceTimer ?? data.sequenceRemainingTime ?? ex.sequenceTimer,
                  currentSequence:
                    data.currentSequence !== undefined
                      ? data.currentSequence
                      : ex.currentSequence,
                },
                data
              )
            : ex
        )
      );
    };

    const onSequence = (data) => {
      if (!data?.te_id) return;
      applyLiveSequencePayload(data);
      const teId = String(data.te_id);
      const seqTimer = data.sequenceTimer ?? data.remaining_sequence_time_seconds;
      const currentSequence =
        data.currentSequence !== undefined ? data.currentSequence : undefined;
      setExercises((prev) =>
        prev.map((ex) =>
          String(ex.te_id) === teId
            ? patchExerciseMediaClock(
                {
                  ...ex,
                  sequenceTimer: seqTimer ?? ex.sequenceTimer,
                  timer:
                    data.timer ?? data.remaining_exercise_time_seconds ?? ex.timer,
                  currentSequence:
                    currentSequence !== undefined
                      ? currentSequence
                      : ex.currentSequence,
                },
                data
              )
            : ex
        )
      );
      const slot = deriveViewerSlot(data, viewerPlayerId, user.user_id);
      setSeqMetaByTeId((prev) => ({
        ...prev,
        [teId]: {
          ...prev[teId],
          lastSequenceCompleted:
            data.last_sequence_completed ?? prev[teId]?.lastSequenceCompleted ?? false,
          viewerRole: slot?.role ?? prev[teId]?.viewerRole ?? null,
          viewerSide: slot?.side ?? prev[teId]?.viewerSide ?? null,
        },
      }));
    };

    const onHr = (payload) => {
      const result = extractViewerHrState(payload, viewerPlayerId, user.user_id);
      if (!result.matched) return;
      // hr_value: 0 (or stale) clears the player's live BPM when strap association moves away.
      setViewerHr(result.bpm);
      if (result.bpm != null && currentTeId) {
        const teId = String(currentTeId);
        setMaxHrByTeId((prev) => {
          const cur = prev[teId] ?? 0;
          return result.bpm > cur ? { ...prev, [teId]: result.bpm } : prev;
        });
      }
    };

    const matchKotTe = (payload) =>
      payload?.te_id && String(payload.te_id) === String(currentTeId);

    const onKotSnapshot = (payload) => {
      if (!matchKotTe(payload)) return;
      setKotSnapshot((prev) => mergeKotSnapshots(prev, payload));
    };

    const onKotMatchSubmitted = (payload) => {
      if (!matchKotTe(payload) || !currentTeId) return;
      setKotSnapshot((prev) =>
        applyKotMatchSubmittedPatch(prev, currentTeId, tsId, payload)
      );
    };

    const onKotRoundCompleted = (payload) => {
      if (!matchKotTe(payload) || !currentTeId) return;
      setKotSnapshot((prev) =>
        applyKotRoundCompletedPatch(prev, currentTeId, tsId, payload)
      );
    };

    const onSessionStatus = (payload) => {
      if (!payload?.ts_id || String(payload.ts_id) !== String(tsId)) return;
      if (
        payload.is_completed ||
        payload.te_state === TE_STATE_FINALIZED ||
        payload.te_state === "finalized"
      ) {
        markSessionFinalized();
      }
      window.dispatchEvent(
        new CustomEvent("smarttt-session-live-status", { detail: payload })
      );
    };

    const onAwaitingSessionDecision = (payload) => {
      if (!payload?.ts_id || String(payload.ts_id) !== String(tsId)) return;
      if (sessionFinalized) return;
      setAwaitingSessionDecision(true);
    };

    const onSessionReopened = (payload) => {
      if (!payload?.ts_id || String(payload.ts_id) !== String(tsId)) return;
      reopenSessionAfterAddExercise();
    };

    socket.on("exerciseStateUpdate", onExerciseState);
    socket.on("timerUpdate", onTimer);
    socket.on("sequenceUpdate", onSequence);
    socket.on("hr-update", onHr);
    socket.on("player_heart_rate", onHr);
    socket.on("playerHeartRate", onHr);
    socket.on("kot:snapshot", onKotSnapshot);
    socket.on("kot:round-stop-requested", onKotSnapshot);
    socket.on("kot:match-submitted", onKotMatchSubmitted);
    socket.on("kot:round-completed", onKotRoundCompleted);
    socket.on("trainingSessionStatusUpdate", onSessionStatus);
    socket.on("trainingSessionAwaitingDecision", onAwaitingSessionDecision);
    socket.on("trainingSessionReopened", onSessionReopened);

    const onSequenceAutoevalSubmitted = (payload) => {
      if (!payload?.sequence_key || !payload?.user_id) return;
      window.dispatchEvent(
        new CustomEvent("smarttt-sequence-autoeval-submitted", {
          detail: payload,
        })
      );
    };
    socket.on("sequenceAutoevalSubmitted", onSequenceAutoevalSubmitted);

    const onLiveTrainingClientConstraints = (payload) => {
      const pid = payload?.ts_id != null ? String(payload.ts_id) : null;
      if (pid && pid !== String(tsId)) return;
      applyForcedConstraints(tsId, payload || {});
    };
    socket.on("liveTrainingClientConstraints", onLiveTrainingClientConstraints);

    const onRosterMaybeChanged = (payload) => {
      if (payload?.ts_id && String(payload.ts_id) !== String(tsId)) return;
      loadRoster({ force: true });
      setViewRefreshToken((token) => token + 1);
    };
    socket.on("playerPresenceUpdate", onRosterMaybeChanged);
    socket.on("playerJoined", onRosterMaybeChanged);
    socket.on("playerLeft", onRosterMaybeChanged);

    return () => {
      socket.off("exerciseStateUpdate", onExerciseState);
      socket.off("timerUpdate", onTimer);
      socket.off("sequenceUpdate", onSequence);
      socket.off("hr-update", onHr);
      socket.off("player_heart_rate", onHr);
      socket.off("playerHeartRate", onHr);
      socket.off("kot:snapshot", onKotSnapshot);
      socket.off("kot:round-stop-requested", onKotSnapshot);
      socket.off("kot:match-submitted", onKotMatchSubmitted);
      socket.off("kot:round-completed", onKotRoundCompleted);
      socket.off("trainingSessionStatusUpdate", onSessionStatus);
      socket.off("trainingSessionAwaitingDecision", onAwaitingSessionDecision);
      socket.off("trainingSessionReopened", onSessionReopened);
      socket.off("sequenceAutoevalSubmitted", onSequenceAutoevalSubmitted);
      socket.off("liveTrainingClientConstraints", onLiveTrainingClientConstraints);
      socket.off("playerPresenceUpdate", onRosterMaybeChanged);
      socket.off("playerJoined", onRosterMaybeChanged);
      socket.off("playerLeft", onRosterMaybeChanged);
    };
  }, [
    socket,
    tsId,
    exercises,
    realtimeByTeId,
    requestRefresh,
    viewerPlayerId,
    user.user_id,
    currentTeId,
    loadRoster,
    markSessionFinalized,
    sessionFinalized,
    reopenSessionAfterAddExercise,
  ]);

  const finalizeTrainingSession = useCallback(() => {
    if (!socket || !tsId) {
      return Promise.resolve({ ok: false, status: "error", message: "missing_socket_or_ts_id" });
    }
    return new Promise((resolve) => {
      socket.emit("finalizeTrainingSession", { ts_id: tsId }, (response) => {
        if (response?.ok || response?.status === "success") {
          markSessionFinalized();
        }
        resolve(response || { ok: false, status: "error" });
      });
    });
  }, [socket, tsId, markSessionFinalized]);

  const clearAwaitingSessionDecision = useCallback(() => {
    setAwaitingSessionDecision(false);
  }, []);

  useEffect(() => {
    if (!tsId || loading) return undefined;
    const id = window.setInterval(() => {
      loadRoster();
    }, 30000);
    return () => window.clearInterval(id);
  }, [tsId, loading, loadRoster]);

  useEffect(() => {
    if (loading || !socket || !tsId || !currentTeId) return undefined;
    if (!currentExercise || !isKotExercise(currentExercise)) return undefined;

    const id = window.setTimeout(() => {
      socket.emit("kot:request-snapshot", { ts_id: tsId, te_id: currentTeId });
    }, 250);
    return () => window.clearTimeout(id);
  }, [loading, socket, tsId, currentTeId, currentExercise?.te_id]);

  useEffect(() => {
    if (!socket || !tsId || !currentTeId) return;
    socket.emit("requestHrSnapshot", { ts_id: tsId, te_id: currentTeId });
  }, [socket, tsId, currentTeId]);

  const loadSessionTables = useCallback(
    async (teId) => {
      if (!grId || !tsId || !teId) return [];
      return fetchSessionTablesDeduped({
        gr_id: grId,
        ts_id: tsId,
        te_id: teId,
      });
    },
    [grId, tsId]
  );

  const reloadViewerTableContext = useCallback(
    async (_exerciseRow, teId) => {
      if (!grId || !tsId || !teId) {
        setSessionTablesRaw([]);
        sessionTablesTeIdRef.current = null;
        return;
      }
      sessionTablesTeIdRef.current = teId;
      const tables = await loadSessionTables(teId);
      if (String(sessionTablesTeIdRef.current) !== String(teId)) return;
      setSessionTablesRaw(tables);
    },
    [grId, tsId, loadSessionTables]
  );

  const applyTableSetupFromSocket = useCallback(
    (data) => {
      if (!data?.ts_id || String(data.ts_id) !== String(tsId)) return;

      if (data.revision != null) {
        applyServerRevisions({ table_setup: data.revision });
      }

      const eventTeId = data.te_id != null ? String(data.te_id) : null;
      const activeTeId = currentTeIdRef.current
        ? String(currentTeIdRef.current)
        : null;
      if (eventTeId && activeTeId && eventTeId !== activeTeId) return;

      if (Array.isArray(data.tables)) {
        if (!data?.te_id || !activeTeId || String(data.te_id) !== activeTeId) {
          return;
        }
        setSessionTablesRaw(
          normalizeSessionTablesForRoster(data.tables, rosterRef.current)
        );
      } else if (activeTeId) {
        setViewRefreshToken((token) => token + 1);
        reloadViewerTableContext(
          currentExerciseRef.current,
          activeTeId
        );
      }

      if (eventTeId) {
        refreshExercises();
      }

      if (socket && tsId && activeTeId) {
        socket.emit("requestHrSnapshot", { ts_id: tsId, te_id: activeTeId });
      }
    },
    [tsId, socket, user.user_id, reloadViewerTableContext, refreshExercises]
  );

  useEffect(() => {
    if (!socket || !tsId) return undefined;

    const onTableSetupChanged = (data) => {
      applyTableSetupFromSocket(data);
    };

    const onPlayerTableChanged = (data) => {
      if (!data?.ts_id || String(data.ts_id) !== String(tsId)) return;
      if (data.has_full_tables) {
        applyTableSetupFromSocket(
          Array.isArray(data.tables)
            ? data
            : { ...data, tables: undefined }
        );
        return;
      }
      applyTableSetupFromSocket(data);
    };

    socket.on("trainingTableSetupChanged", onTableSetupChanged);
    socket.on("playerTableChanged", onPlayerTableChanged);

    return () => {
      socket.off("trainingTableSetupChanged", onTableSetupChanged);
      socket.off("playerTableChanged", onPlayerTableChanged);
    };
  }, [socket, tsId, applyTableSetupFromSocket]);

  const refreshLiveView = useCallback(
    async ({ silent = false } = {}) => {
      if (!tsId || !socket) {
        if (!silent) {
          setError("Unable to refresh — connection unavailable");
        }
        return;
      }
      if (refreshInFlightRef.current && silent) return;

      const now = Date.now();
      if (silent && now - lastRefreshAtRef.current < 400) return;
      lastRefreshAtRef.current = now;

      const generation = ++refreshGenerationRef.current;
      const isStale = () => generation !== refreshGenerationRef.current;

      refreshInFlightRef.current = true;
      if (!silent) setRefreshing(true);

      try {
        const role = isCoachViewForTraining(user, permissions) ? "coach" : "player";
        socket.emit("joinTrainingSession", {
          ts_id: tsId,
          user_id: user.user_id,
          role,
        });

        const [sortedExercises, , subgroupRows] = await Promise.all([
          refreshExercises({ skipIndexUpdate: true }),
          loadRoster({ force: true }),
          role === "coach" ? loadSubgroups() : Promise.resolve([]),
        ]);
        if (isStale()) return;

        let exercisesForContext = Array.isArray(sortedExercises) ? sortedExercises : [];
        const stationsForContext = buildLiveStations({
          subgroups: Array.isArray(subgroupRows) ? subgroupRows : subgroups,
          exercises: exercisesForContext,
        });

        await Promise.race([
          requestSessionSync(socket, {
            tsId,
            onDomains: (domains) => {
              if (isStale()) return;
              setExercises((prev) => {
                const patched = patchExercisesFromSyncDomains(prev, domains);
                exercisesForContext = patched;
                return patched;
              });
              if (domains?.exercise_timer_snapshots) {
                setCurrentSequenceIndexByTeId((prev) =>
                  mergeSequenceIndexFromTimerSnaps(
                    prev,
                    domains.exercise_timer_snapshots
                  )
                );
              }
            },
          }),
          new Promise((resolve) => {
            window.setTimeout(resolve, 12000);
          }),
        ]);
        if (isStale()) return;

        const freshRealtime = buildRealtimeMapFromExerciseRows(exercisesForContext);
        setRealtimeByTeId(freshRealtime);
        realtimeByTeIdRef.current = freshRealtime;

        const isCoach = role === "coach";
        let coachTsgId = focusedTsgIdRef.current;
        if (isCoach && hasMultipleLiveStations(stationsForContext)) {
          coachTsgId = resolveFocusedTsgId(stationsForContext, coachTsgId);
          setFocusedTsgId(coachTsgId);
        } else if (isCoach) {
          coachTsgId = resolveFocusedTsgId(stationsForContext, coachTsgId);
          if (coachTsgId) setFocusedTsgId(coachTsgId);
        }

        let resolvedIdx = pickCoachStationControlIndex(
          exercisesForContext,
          freshRealtime,
          isCoach ? coachTsgId : null
        );
        if (resolvedIdx < 0) resolvedIdx = 0;
        if (!isCoach) {
          const probeTe = exercisesForContext[resolvedIdx];
          if (probeTe?.te_id) {
            const probeTables = await loadSessionTables(probeTe.te_id);
            if (isStale()) return;
            const tsgId = findViewerSubgroupId({
              tables: probeTables,
              userId: user?.user_id,
              playerId: viewerPlayerIdRef.current,
            });
            if (tsgId) {
              resolvedIdx = pickStationScopedExerciseIndex(
                exercisesForContext,
                freshRealtime,
                tsgId
              );
            }
          }
        }
        setCurrentIndex(resolvedIdx);

        const activeExercise = exercisesForContext[resolvedIdx] || null;
        const teId = activeExercise?.te_id;
        const prevTeId = currentTeIdRef.current;

        if (teId && String(teId) !== String(prevTeId)) {
          setKotSnapshot(null);
          setSeqMetaByTeId({});
          setSessionTablesRaw([]);
          sessionTablesTeIdRef.current = teId;
        }

        if (teId) {
          sessionTablesTeIdRef.current = teId;
          await requestSessionSync(socket, {
            tsId,
            activeTeId: teId,
            onDomains: (domains) => {
              if (isStale()) return;
              // Restore live timers/currentSequence after API refresh wiped them.
              setExercises((prev) => patchExercisesFromSyncDomains(prev, domains));
              if (domains?.exercise_timer_snapshots) {
                setCurrentSequenceIndexByTeId((prev) =>
                  mergeSequenceIndexFromTimerSnaps(
                    prev,
                    domains.exercise_timer_snapshots
                  )
                );
              }
              if (domains?.kot_snapshots) {
                const snap = domains.kot_snapshots[String(teId)];
                if (snap) setKotSnapshot((p) => mergeKotSnapshots(p, snap));
              }
            },
          });
          if (isStale()) return;

          socket.emit("requestHrSnapshot", { ts_id: tsId, te_id: teId });
          if (activeExercise && isKotExercise(activeExercise)) {
            socket.emit("kot:request-snapshot", { ts_id: tsId, te_id: teId });
          }
          await loadSessionTables(teId).then((tables) => {
            if (!isStale() && String(sessionTablesTeIdRef.current) === String(teId)) {
              setSessionTablesRaw(tables);
            }
          });
        } else {
          setSessionTablesRaw([]);
          setKotSnapshot(null);
          sessionTablesTeIdRef.current = null;
        }

        if (isStale()) return;
        setError(null);
        setViewRefreshToken((token) => token + 1);
      } catch (e) {
        if (!isStale()) {
          setError(e?.message || "Failed to refresh live training");
        }
      } finally {
        if (!isStale()) {
          refreshInFlightRef.current = false;
          if (!silent) setRefreshing(false);
        }
      }
    },
    [
      tsId,
      socket,
      user.user_id,
      user,
      permissions,
      refreshExercises,
      loadRoster,
      loadSubgroups,
      loadSessionTables,
      setFocusedTsgId,
      subgroups,
    ]
  );

  useEffect(() => {
    if (!tsId) return undefined;

    const runSilentRefresh = () => {
      if (!allowAutoRefreshRef.current) return;
      refreshLiveView({ silent: true });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runSilentRefresh();
      }
    };

    const onPageShow = (event) => {
      if (event.persisted) {
        runSilentRefresh();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", runSilentRefresh);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", runSilentRefresh);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [tsId, refreshLiveView]);

  const exerciseStateId = currentExercise
    ? resolveExerciseStateIdFromRow(currentExercise, realtimeByTeId)
    : null;

  const isKot = currentExercise ? isKotExercise(currentExercise) : false;
  const finalizedCount = exercises.filter(
    (ex) => resolveExerciseStateIdFromRow(ex, realtimeByTeId) === TE_STATE_FINALIZED
  ).length;

  const myTablePlayer = useMemo(() => {
    if (!viewerPlayerId || !roster.length) return null;
    return roster.find((p) => String(p.player_id) === String(viewerPlayerId)) || null;
  }, [viewerPlayerId, roster]);

  const currentExerciseForTable = currentExercise;

  useEffect(() => {
    if (!grId || !tsId || !currentTeId) {
      setSessionTablesRaw([]);
      sessionTablesTeIdRef.current = null;
      return undefined;
    }
    sessionTablesTeIdRef.current = currentTeId;
    let cancelled = false;
    (async () => {
      try {
        const tables = await loadSessionTables(currentTeId);
        if (cancelled) return;
        if (String(sessionTablesTeIdRef.current) !== String(currentTeId)) return;
        setSessionTablesRaw(tables);
      } catch {
        if (!cancelled && String(sessionTablesTeIdRef.current) === String(currentTeId)) {
          setSessionTablesRaw([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [grId, tsId, currentTeId, viewRefreshToken, loadSessionTables]);

  const sessionTables = useMemo(
    () => normalizeSessionTablesForRoster(sessionTablesRaw, roster),
    [sessionTablesRaw, roster]
  );

  const viewerTableContext = useMemo(() => {
    if (!currentTeId || !sessionTables.length) return null;
    const tables = sessionTables;
    return resolveViewerTableContext(
      tables,
      viewerPlayerId,
      user.user_id,
      currentExerciseForTable,
      roster
    );
  }, [
    currentTeId,
    sessionTables,
    viewerPlayerId,
    user.user_id,
    currentExerciseForTable,
  ]);

  const viewerTableNumber = viewerTableContext?.tableNumber ?? null;

  const currentSeqMeta = currentTeId ? seqMetaByTeId[String(currentTeId)] : null;
  const viewerRole = currentSeqMeta?.viewerRole ?? null;
  const viewerSide = currentSeqMeta?.viewerSide ?? null;
  const awaitingManualSequenceStart =
    currentSeqMeta?.awaitingManualSequenceStart ?? false;
  const awaitingAutoSequenceStart =
    currentSeqMeta?.awaitingAutoSequenceStart ?? false;
  const sequenceTransitionPauseRemaining =
    currentSeqMeta?.sequenceTransitionPauseRemaining ?? null;
  const lastSequenceCompleted =
    currentSeqMeta?.lastSequenceCompleted ?? false;
  const viewerMaxHr = currentTeId
    ? maxHrByTeId[String(currentTeId)] ?? viewerHr ?? null
    : viewerHr ?? null;

  const { getSamples: getHrSamples, getLiveBpm, requestHrSnapshot } = useLiveHrHistory({
    socket,
    tsId,
    currentTeId,
    enabled: Boolean(socket && tsId),
  });

  return {
    socket,
    user,
    exercises,
    stations,
    multiStation,
    focusedTsgId,
    selectFocusedStation,
    currentExercise,
    currentIndex,
    setCurrentIndex,
    exerciseStateId,
    isKot,
    kotSnapshot,
    roster,
    viewerPlayerId,
    myTablePlayer,
    viewerTableNumber,
    viewerTableContext,
    sessionTables,
    viewerHr,
    viewerMaxHr,
    getHrSamples,
    getLiveBpm,
    requestHrSnapshot,
    liveSequenceRolesByTeId,
    socketSequenceMetaByTeId,
    currentSequenceIndexByTeId,
    viewerRole,
    viewerSide,
    awaitingManualSequenceStart,
    awaitingAutoSequenceStart,
    sequenceTransitionPauseRemaining,
    lastSequenceCompleted,
    loading,
    error,
    finalizedCount,
    realtimeByTeId,
    sessionFinalized,
    awaitingSessionDecision,
    finalizeTrainingSession,
    clearAwaitingSessionDecision,
    reopenSessionAfterAddExercise,
    requestRefresh,
    refreshExercises,
    refreshLiveView,
    refreshing,
    viewRefreshToken,
  };
}
